"use client";

// Ported from relay-protocol packages/relay-widget/src/components/DepositStep.tsx
// (v0.1.0), renamed DepositStepView.tsx to avoid a name collision with Task
// 11's wizard step of the same concept. Mechanical substitution only: CSS
// Modules `styles.x` references become literal "rd-x" class names (see
// ../styles/component.css's header mapping table), the CSS Modules import
// becomes the plain side-effect import below, viem's `formatUnits`/
// `zeroAddress` are replaced by ../lib/units's self-implemented equivalents
// (dapp-ui may depend on neither wagmi nor viem), `ChainKind` now comes from
// ../lib/types, and `DepositState` now comes from ../hooks/useDepositAddress
// (the ported hook, which exports the same shape). `qrcode.react`'s
// `QRCodeSVG` import is unchanged -- already a dapp-ui dependency. Everything
// else -- props, EIP-681/Solana Pay URI building, copy-to-clipboard, and
// layout -- is the original logic, untouched.
//
// Behavior: svm (Solana) origins render a display-only deposit -- no
// one-click button ever, a `solana:` Solana Pay QR, and (when present) the
// exact human amount. EVM origins get a one-click deposit button ONLY when
// `canDeposit` is true (i.e. the caller entered a usable amount) -- an
// amount-less "send anything" deposit has nothing for a wallet tx to carry,
// so it always falls back to the manual QR/address flow below.
//
// 2026-07-30 redesign (approved step-2 "variant A"), presentation only -- same
// props, same URI building, same copy handler, same gating:
//   * the QR is the hero: a 172px white frame near the top of the step instead
//     of buried under two explainer paragraphs, which is what a user scanning
//     with a phone actually looks for;
//   * the "Deposit address" card title and the `.rd-card` wrapper are gone (the
//     progress bar names the step now, and the block IS the step);
//   * the address chip keeps its mono styling and accent Copy button;
//   * the two explainer paragraphs collapse into ONE line under the address,
//     and the long wrong-token warning into one sentence plus a collapsed
//     `<details>` ("More about this address") holding the original wording.
//
// 2026-07-30 follow-up: the address is rendered IN FULL again. The redesign
// middle-truncated it to `0x432f…b3159` to keep it on one mono line, but a
// deposit address is the one value on this screen a user needs to VERIFY
// character-by-character against what their exchange/wallet shows -- an
// abbreviation they have to hover or copy to check defeats that. It now wraps
// across two lines instead (`word-break: break-all` on the chip's text), which
// costs a line of height and nothing else: the accent Copy button and the
// full-address clipboard write are unchanged, and `title`/aria-label stay for
// AT and for hosts reading the value off the DOM.
//
// 2026-07-31 one-page redesign: the SEND RULES left this file for
// `DepositChecklist.tsx`, which states all three of them once, in a card under
// the address. Gone from here with them: the `.rd-address-warning` callout ("Only
// send X on Y") and its `hideTokenWarning` prop, the `minAmount` prop's "deposits
// below N may not be swept" hint, and the amount-less "reusable address -- send
// any amount" sentence. The long "More about this address" wording now lives in
// the info tooltip beside the Deposit address label, so it stays attached to
// the value it explains without reserving a detached block at the bottom.
import { useEffect, useId, useState } from "react";
import { formatUnits, zeroAddress } from "../lib/units";
import { ChainIcon } from "./icons";
import type { ChainKind } from "../lib/types";
import type { DepositState } from "../hooks/useDepositAddress";
import "../styles/component.css";
import { QRCodeSVG } from "qrcode.react";

export interface DepositStepViewProps {
  depositAddress: string;
  originChainId: number;
  originChainName: string;
  /** Token contract address, or the zero address for the chain's native asset. */
  originCurrency: string;
  /** Smallest-unit base-10 integer string, or undefined/"" when no amount was entered --
   * this is a reusable address, so the deposit works with or without one. */
  amount?: string;
  tokenSymbol: string;
  /** Origin token decimals -- used to render the exact human amount to send. */
  tokenDecimals: number;
  /** Origin chain VM family; "svm" (Solana) renders a display-only deposit. */
  kind?: ChainKind;
  /** EVM one-click deposit state, from useDepositAddress's `depositState`. */
  depositState: DepositState;
  /** EVM one-click deposit action, from useDepositAddress's `deposit`. No-op for svm origins. */
  onDeposit: () => void;
  /** True only when a usable amount was entered (and the origin is EVM) -- gates whether the
   * one-click button renders at all. From useDepositAddress's `canDeposit`. */
  canDeposit: boolean;
  /** False when the host disabled one-click outright: the "enter an amount for
   * a one-click deposit" helper line then never mentions it. Default true. */
  oneClickEnabled?: boolean;
}

/** Holds exactly the QR + address card footprint while either config or a
 * deposit address is loading. It deliberately reuses `.rd-deposit-head`, so
 * the same side-by-side/wrapped layout decision applies before and after data
 * arrives and the modal never changes height at that boundary. */
export function DepositAddressSkeleton() {
  return (
    <div className="rd-deposit-head" data-testid="rd-address-skeleton" aria-hidden="true">
      <span className="rd-skeleton rd-skeleton-qr" />
      <span className="rd-skeleton rd-skeleton-address" />
    </div>
  );
}

/** EIP-681 payment URI so compatible EVM wallets can prefill the transfer from a QR scan.
 * `amountWei` is optional -- omitted entirely for an amount-less "send any amount" deposit. */
function buildPaymentUri(params: {
  depositAddress: string;
  originChainId: number;
  originCurrency: string;
  amountWei?: bigint;
  isNative: boolean;
}): string {
  const { depositAddress, originChainId, originCurrency, amountWei, isNative } = params;
  if (isNative) {
    return amountWei !== undefined
      ? `ethereum:${depositAddress}@${originChainId}?value=${amountWei.toString()}`
      : `ethereum:${depositAddress}@${originChainId}`;
  }
  const query = new URLSearchParams({ address: depositAddress });
  if (amountWei !== undefined) query.set("uint256", amountWei.toString());
  return `ethereum:${originCurrency}@${originChainId}/transfer?${query.toString()}`;
}

/**
 * Solana Pay URI so a Solana wallet (Phantom, ...) can prefill the SPL-token
 * transfer from a QR scan: solana:<recipient>?amount=<decimal>&spl-token=<mint>.
 * amount is a decimal UI amount (not smallest units).
 */
function buildSolanaPayUri(params: { depositAddress: string; splMint: string; humanAmount: string }): string {
  const { depositAddress, splMint, humanAmount } = params;
  const q = new URLSearchParams();
  if (humanAmount) q.set("amount", humanAmount);
  q.set("spl-token", splMint);
  return `solana:${depositAddress}?${q.toString()}`;
}

export function DepositStepView({
  depositAddress,
  originChainId,
  originChainName,
  originCurrency,
  amount,
  tokenSymbol,
  tokenDecimals,
  kind = "evm",
  depositState,
  onDeposit,
  canDeposit,
  oneClickEnabled = true,
}: DepositStepViewProps) {
  const [copied, setCopied] = useState(false);
  const [compactView, setCompactView] = useState<"address" | "qr">("address");
  const addressTooltipId = useId();

  // A newly issued address always returns the compact/mobile presentation to
  // its safe default: readable address + copy action, with QR opt-in.
  useEffect(() => {
    setCompactView("address");
  }, [depositAddress]);

  const isNative = originCurrency.toLowerCase() === zeroAddress;
  const isSvm = kind === "svm";

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(depositAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API unavailable (insecure context, permissions, ...) --
      // the address is still selectable/visible as plain text.
    }
  }

  // Exact human amount to send (e.g. "6.9"), only when an amount was
  // actually entered -- this is a reusable address, so "no amount" is a
  // perfectly valid, common state (send-any-amount).
  let amountWei: bigint | undefined;
  if (amount) {
    try {
      const parsed = BigInt(amount);
      if (parsed > 0n) amountWei = parsed;
    } catch {
      /* malformed -- treat as no amount */
    }
  }
  const hasAmount = amountWei !== undefined;
  let humanAmount: string | undefined;
  if (hasAmount) {
    try {
      humanAmount = formatUnits(amountWei as bigint, tokenDecimals);
    } catch {
      humanAmount = amount;
    }
  }

  const paymentUri = isSvm
    ? buildSolanaPayUri({ depositAddress, splMint: originCurrency, humanAmount: humanAmount ?? "" })
    : buildPaymentUri({ depositAddress, originChainId, originCurrency, amountWei, isNative });

  const { isSubmitting, isConfirming, isConfirmed, needsSwitch, txHash, reverted, error } = depositState;

  return (
    <section className="rd-stack">
      {/* The QR and the address are ONE row (2026-07-31 user revision): they are
          two encodings of the same value, and stacking them pushed everything the
          user might want to change (origin, delivery token) a full QR height down
          the column. QR left at its natural 172px frame, the chip + Copy right in
          a flexible column that wraps the address across lines as needed; the row
          On a narrow drawer the address/copy card becomes the default pane and
          its QR icon switches to the QR pane (see `.rd-deposit-head`), avoiding
          the old full-height vertical stack.

          Center badge: the ORIGIN CHAIN's logo overlaid on the QR, so two QRs for
          different chains are tellable apart at a glance (a wrong-chain scan is
          this screen's most expensive mistake). Error correction is raised to "H"
          (~30% recoverable) -- the 36px badge covers ~6% of the 144px code, so
          scanning is unaffected. */}
      <div className="rd-deposit-head" data-compact-view={compactView}>
        <div className="rd-deposit-qr-pane">
          <div className="rd-deposit-qr">
            <QRCodeSVG value={paymentUri} size={144} level="H" />
            <span className="rd-deposit-qr-badge" aria-hidden="true">
              <ChainIcon chainId={originChainId} chainName={originChainName} size={28} />
            </span>
          </div>
          <button
            type="button"
            className="rd-mobile-address-toggle"
            aria-label="Show address and copy"
            onClick={() => setCompactView("address")}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="8" y="8" width="11" height="11" rx="2.25" stroke="currentColor" strokeWidth="1.8" />
              <path d="M16 8V6.25A2.25 2.25 0 0 0 13.75 4h-7.5A2.25 2.25 0 0 0 4 6.25v7.5A2.25 2.25 0 0 0 6.25 16H8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Rendered IN FULL (2026-07-30 follow-up): this is the value a user
            verifies character-by-character against their exchange/wallet, so it
            wraps across lines rather than abbreviating. `title` is kept even
            though it now duplicates the text -- it costs nothing and hosts/AT
            already read the address off it. */}
        <div className="rd-deposit-address">
          <span className="rd-deposit-address-label">
            <button
              type="button"
              className="rd-address-term"
              aria-describedby={addressTooltipId}
            >
              Deposit address
              <span id={addressTooltipId} className="rd-address-tooltip" role="tooltip">
                This is a reusable, open deposit address. A wrong token or wrong chain will not be processed
                automatically, and recovery has weaker guarantees than a one-time order address and may require a
                manual request.
              </span>
            </button>
          </span>
          <button
            type="button"
            className="rd-mobile-qr-toggle"
            aria-label="Show deposit QR code"
            aria-pressed={compactView === "qr"}
            onClick={() => setCompactView("qr")}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path
                d="M7.913 10.833c.69 0 1.25.56 1.25 1.25v4.163c0 .69-.56 1.25-1.25 1.25H3.75c-.69 0-1.25-.56-1.25-1.25v-4.163c0-.69.56-1.25 1.25-1.25h4.163Zm6.255 6.663h-1.665V15.83h1.665v1.666Zm3.332 0h-1.666V15.83H17.5v1.666Zm-13.334-5v3.334h3.331v-3.333H4.166Zm8.337 3.334h-1.666v-1.666h1.666v1.666Zm3.331 0h-1.666v-1.666h1.666v1.666Zm-1.666-1.666h-1.665v-1.667h1.665v1.667Zm3.332 0h-1.666v-1.667H17.5v1.667Zm-4.997-1.667h-1.666v-1.664h1.666v1.664Zm3.331 0h-1.666v-1.664h1.666v1.664ZM7.913 2.504c.69 0 1.25.56 1.25 1.25v4.163c0 .69-.56 1.25-1.25 1.25H3.75c-.69 0-1.25-.56-1.25-1.25V3.754c0-.69.56-1.25 1.25-1.25h4.163Zm8.329 0c.69 0 1.25.56 1.25 1.25v4.163c0 .69-.56 1.25-1.25 1.25h-4.163c-.69 0-1.25-.56-1.25-1.25V3.754c0-.69.56-1.25 1.25-1.25h4.163ZM4.166 4.17V7.5h3.331V4.17H4.166Zm8.329 0V7.5h3.331V4.17h-3.331Z"
                fill="currentColor"
              />
            </svg>
          </button>
          <span className="rd-address-full" title={depositAddress}>
            {depositAddress}
          </span>
          <button
            type="button"
            className="rd-address-copy"
            onClick={handleCopy}
            aria-label={`Copy deposit address ${depositAddress}`}
          >
            {copied ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="m5 12 4 4L19 6"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="8" y="8" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="1.8" />
                <path
                  d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            )}
            <span aria-live="polite">{copied ? "Copied" : "Copy address"}</span>
          </button>
        </div>
      </div>

      {/* EVM one-click deposit only makes sense once an amount was entered --
          an amount-less deposit has nothing for the wallet tx to carry. SVM
          (Solana) origins are always display-only: an EVM wallet cannot sign
          Solana, so the user pays out-of-band from a Solana wallet. */}
      {!isSvm && canDeposit && (
        <>
          <button
            type="button"
            className="rd-button-primary rd-button-block"
            onClick={onDeposit}
            // A REVERTED tx also reaches isConfirmed, so gating on isConfirmed
            // alone disabled the button in exactly the state the message below
            // tells the user to try again -- the flow could only be recovered by
            // remounting the widget (upstream 07354b7). A revert leaves the
            // funds untouched, so retrying is both safe and the obvious next step.
            disabled={isSubmitting || isConfirming || (isConfirmed && !reverted)}
          >
            {isConfirmed && !reverted
              ? "Deposit sent"
              : isConfirming
                ? "Confirming..."
                : isSubmitting
                  ? needsSwitch
                    ? "Switching network..."
                    : "Confirm in wallet..."
                  : `One-click deposit ${tokenSymbol}${needsSwitch ? ` (switch to ${originChainName})` : ""}`}
          </button>

          {error && <div className="rd-callout rd-callout-danger">{error}</div>}

          {txHash && (
            <p className="rd-card-hint">
              Tx: <span style={{ fontFamily: "monospace" }}>{txHash}</span>
            </p>
          )}

          {isConfirmed && reverted && (
            <div className="rd-callout rd-callout-danger">
              The deposit transaction reverted on-chain. Please check your wallet and try again.
            </div>
          )}

          {isConfirmed && !reverted && (
            <div className="rd-callout rd-callout-success">
              Deposit confirmed on-chain. Waiting for it to be swept...
            </div>
          )}

          {/* (The "or deposit manually" divider left with the 2026-07-31 user
              revision: the QR/address row is ABOVE this button now, so there is
              no manual path below it to divide from.) */}
        </>
      )}

      {isSvm && (
        <div className="rd-callout rd-callout-muted">
          Pay from a Solana wallet (e.g. Phantom). Scan the QR or copy the address above and send{" "}
          {hasAmount ? (
            <>
              exactly{" "}
              <strong>
                {humanAmount} {tokenSymbol}
              </strong>
            </>
          ) : (
            <>
              any amount of <strong>{tokenSymbol}</strong>
            </>
          )}
          .
        </div>
      )}

      {/* Amount-specific only. The amount-LESS wording ("reusable address, send
          any amount") moved into the checklist card, so this line is silent unless
          there is an exact figure to name (or a one-click nudge to make). */}
      {(hasAmount || (!isSvm && !canDeposit && oneClickEnabled)) && (
        <p className="rd-card-hint">
          {hasAmount && (
            <>
              Send exactly{" "}
              <strong>
                {humanAmount} {tokenSymbol}
              </strong>{" "}
              on {originChainName} to this address.
            </>
          )}
          {!isSvm && !canDeposit && oneClickEnabled && !hasAmount && "Enter an amount to deposit in one click."}
        </p>
      )}

    </section>
  );
}
